import { Injectable } from '@angular/core';
import { Network } from '@capacitor/network';
import { AsyncSubject, BehaviorSubject, Subject, Subscription, timer } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { AlertController, ToastController } from '@ionic/angular';
import { TranslateUtilityService } from './translation-utility.service';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root'
})
export class NetworkService {
  private networkStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private networkDisconnected: Subject<void> = new Subject<void>();
  private disconnectedSubscription!: Subscription;
  private isAlertShown = false;
  private alert: HTMLIonAlertElement | null = null;

  constructor(
    private alertController: AlertController,
    private toastController: ToastController,
    private translate: TranslateUtilityService
  ) {

    if (PlatformService.isBrowser()) return;

    // Initialize network status
    this.initNetworkStatus();
    this.setupNetworkListeners();

    // Setup debounce for network disconnection
    this.disconnectedSubscription = this.networkDisconnected
      .pipe(debounce(() => timer(3000)))
      .subscribe(() => {
        this.showNetworkDisconnectedAlert();
      });
  }

  public init() {
    let ret = new AsyncSubject<boolean>();
    ret.next(true);
    ret.complete();
    return ret;
  }

  private initNetworkStatus(): void {
    // Get initial network status
    Network.getStatus().then(status => {
      this.networkStatus.next(status.connected);
    });
  }

  private setupNetworkListeners(): void {
    // Listen for network status changes
    Network.addListener('networkStatusChange', (status) => {
      this.networkStatus.next(status.connected);

      if (!status.connected) {
        // Network disconnected - trigger the subject for debounce
        this.networkDisconnected.next();
      } else {
        // Network reconnected
        if (this.isAlertShown) {
          this.dismissAlert();
          this.showReconnectedToast();
        }
      }
    });
  }

  private async showNetworkDisconnectedAlert(): Promise<void> {
    this.alert = await this.alertController.create({
      header: this.translate.instant("NETWORK_STATUS.DISCONNECTED_TITLE"),
      message: this.translate.instant("NETWORK_STATUS.DISCONNECTED_MESSAGE"),
      buttons: [this.translate.instant("OK")]
    });

    this.isAlertShown = true;
    await this.alert.present();
  }

  private dismissAlert(): void {
    if (this.alert) {
      this.alert.dismiss();
      this.alert = null;
      this.isAlertShown = false;
    }
  }

  private async showReconnectedToast(): Promise<void> {
    const toast = await this.toastController.create({
      message: this.translate.instant("NETWORK_STATUS.RECONNECTED"),
      duration: 3000,
      position: 'bottom'
    });

    await toast.present();
  }

  public get isConnected(): boolean {
    return this.networkStatus.getValue();
  }

  public get isConnected$(): BehaviorSubject<boolean> {
    return this.networkStatus;
  }

  ngOnDestroy(): void {
    if (this.disconnectedSubscription) {
      this.disconnectedSubscription.unsubscribe();
    }
    Network.removeAllListeners();
  }
}
